/**
 * What the referential contributes to the route tree.
 *
 * Each route declares the permission it needs, and the same declaration drives
 * both the router's gate and the navigation entry — so a menu item cannot
 * appear for a page the user would be refused.
 *
 * The permissions are split the way the backend splits them, by consequence
 * rather than by table. Reading articles, editing them and publishing them are
 * three different jobs held by three different roles in the design's matrix,
 * and the article screen renders the publish button from the third one alone.
 *
 * `/referentiel/articles/$id` carries no `nav` entry: it is a destination
 * reached from the list, not a menu item.
 */

import type { ComponentType } from "react";
import { useParams } from "@tanstack/react-router";
import type { ApiClient } from "@hardauto/core-api";
import type { FeatureRoutes } from "@hardauto/core-router";

import { ArticleScreen } from "./screens/ArticleScreen";
import { ArticlesScreen } from "./screens/ArticlesScreen";
import { FamiliesScreen } from "./screens/FamiliesScreen";
import { PricingScreen } from "./screens/PricingScreen";
import { SitesScreen } from "./screens/SitesScreen";
import { StockByDepotScreen } from "./screens/StockByDepotScreen";
import { StockScreen, type CategoryFilterProps } from "./screens/StockScreen";
import { REFERENTIAL_PATHS } from "./paths";
import { useLocalScope, type ReferentialScope } from "./scope";
import "./i18n";

export interface ReferentialRouteOptions {
  /**
   * Called after the structure screen changed a company, a point of sale or
   * a depot. The application uses it to read the structure again wherever
   * it keeps a copy — the working context lives outside this package.
   */
  onStructureChanged?: () => void;
  /**
   * The working-context bar's current selection. The application injects
   * its own; without it, screens fall back to "every company, no depot" —
   * the same convention `catalogRoutes` uses for `useScope`.
   */
  useScope?: () => ReferentialScope;
  /**
   * The web catalogue's tree, shown beside « Articles par dépôt » to narrow it by
   * category. The tree is the catalogue's feature, so the application injects it; without
   * one the page simply has no category filter.
   */
  CategoryFilter?: ComponentType<CategoryFilterProps>;
}

export function referentialRoutes(
  client: ApiClient,
  options: ReferentialRouteOptions = {},
): FeatureRoutes {
  const { onStructureChanged } = options;
  const useScope = options.useScope ?? useLocalScope;

  function Articles() {
    return <ArticlesScreen client={client} scope={useScope()} />;
  }

  function Article() {
    // `strict: false` because the route tree is assembled at runtime from a
    // registry, so TanStack cannot infer per-route parameter types — see the
    // note in `core-router/create-router.tsx`. The id is validated by the
    // server on every call it is used in.
    const params: { id?: string } = useParams({ strict: false });
    return <ArticleScreen client={client} id={params.id ?? ""} />;
  }

  function Families() {
    return <FamiliesScreen client={client} />;
  }

  function Sites() {
    return <SitesScreen client={client} onStructureChanged={onStructureChanged} />;
  }

  // One screen, three doors: the structure's levels are separate menu
  // entries in the back office's Administration, and each opens the screen
  // on its own tab. The tabs stay, because the tree view adds them up.
  function Companies() {
    return (
      <SitesScreen client={client} initialTab="companies" onStructureChanged={onStructureChanged} />
    );
  }

  function PointsOfSale() {
    return (
      <SitesScreen client={client} initialTab="points" onStructureChanged={onStructureChanged} />
    );
  }

  function Depots() {
    return (
      <SitesScreen client={client} initialTab="depots" onStructureChanged={onStructureChanged} />
    );
  }

  function Stock() {
    return (
      <StockScreen
        client={client}
        scope={useScope()}
        {...(options.CategoryFilter ? { CategoryFilter: options.CategoryFilter } : {})}
      />
    );
  }

  function StockByDepot() {
    return <StockByDepotScreen client={client} />;
  }

  function Pricing() {
    return <PricingScreen client={client} />;
  }

  return {
    id: "referential",
    routes: [
      {
        path: REFERENTIAL_PATHS.articles,
        component: Articles,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.article.read"] },
        nav: {
          group: "referential:group",
          labelKey: "referential:nav.articles",
          section: "main",
          order: 20,
        },
      },
      {
        path: REFERENTIAL_PATHS.article,
        component: Article,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.article.read"] },
      },
      {
        path: REFERENTIAL_PATHS.families,
        component: Families,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.family.read"] },
        nav: {
          group: "referential:group",
          labelKey: "referential:nav.families",
          section: "main",
          order: 21,
        },
      },
      {
        // The former single entry, kept as an address: bookmarks and links
        // written before the three entries below still land somewhere.
        path: REFERENTIAL_PATHS.sites,
        component: Sites,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.site.read"] },
      },
      {
        path: REFERENTIAL_PATHS.companies,
        component: Companies,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.site.read"] },
        nav: {
          group: "referential:group",
          labelKey: "referential:nav.companies",
          section: "main",
          order: 22,
        },
      },
      {
        path: REFERENTIAL_PATHS.pointsOfSale,
        component: PointsOfSale,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.site.read"] },
        nav: {
          group: "referential:group",
          labelKey: "referential:nav.pointsOfSale",
          section: "main",
          order: 22.1,
        },
      },
      {
        path: REFERENTIAL_PATHS.depots,
        component: Depots,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.site.read"] },
        nav: {
          group: "referential:group",
          labelKey: "referential:nav.depots",
          section: "main",
          order: 22.2,
        },
      },
      {
        path: REFERENTIAL_PATHS.stockByDepot,
        component: StockByDepot,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.stock.read"] },
        nav: {
          group: "referential:group",
          labelKey: "referential:nav.stockByDepot",
          section: "main",
          order: 22.9,
        },
      },
      {
        path: REFERENTIAL_PATHS.stock,
        component: Stock,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.stock.read"] },
        nav: {
          group: "referential:group",
          labelKey: "referential:nav.stock",
          section: "main",
          order: 23,
        },
      },
      {
        path: REFERENTIAL_PATHS.pricing,
        component: Pricing,
        layout: "app",
        requirement: { authenticated: true, permissions: ["referential.pricing.read"] },
        nav: {
          group: "referential:group",
          labelKey: "referential:nav.pricing",
          section: "main",
          order: 24,
        },
      },
    ],
  };
}
